/**
 * The one count of basic attacks a skill's action lock displaces.
 *
 * A skill may start in the basic attack's RECOVERY and the basic cycle keeps running, so only the part of the action
 * beyond that recovery costs basic hits. The result is a COUNT OF BASIC HITS of the displaced source; each consumer
 * converts it into its own unit through that source's own hit damage (craft pricing: damage; cadence floor and mana
 * increment: the cadence calibration's hit equivalents; evaluation: SVP).
 */

import { NonFiniteNumericError } from '../math.js';
import type { PricingWeaponId } from '../reference/weapon-progression-v0.js';
import { resolveBasicAttackPhaseTiming } from '../sim/basic-attack-timing.js';

/** The basic attack an action lock displaces: its hit, its cycle and the recovery a skill may start in. */
export interface DisplacedBasicSource {
  readonly hitDamage: number;
  readonly cycleS: number;
  readonly recoveryS: number;
}

export function displacedBasicHits(actionTimeS: number, basic: Pick<DisplacedBasicSource, 'cycleS' | 'recoveryS'>): number {
  for (const [name, value] of [['actionTimeS', actionTimeS], ['recoveryS', basic.recoveryS], ['cycleS', basic.cycleS]] as const) {
    if (!Number.isFinite(value)) throw new NonFiniteNumericError(`${name} must be finite (got ${value})`);
  }
  if (actionTimeS < 0) throw new Error(`actionTimeS must be finite >= 0 (got ${actionTimeS})`);
  if (basic.recoveryS < 0) throw new Error(`recoveryS must be finite >= 0 (got ${basic.recoveryS})`);
  if (basic.cycleS <= 0) throw new Error(`cycleS must be finite > 0 (got ${basic.cycleS})`);
  return Math.max(0, actionTimeS - basic.recoveryS) / basic.cycleS;
}

/**
 * The recovery a skill may start in, for a pricing weapon's basic attack. The catalyst basic (and the intrinsic one)
 * split their cycle with the Bow phase seed (`authoritative-world-core.ts`), so the MAGIC basis takes its recovery from the same row.
 */
export function resolveBasicRecoveryS(weaponId: PricingWeaponId, cycleS: number): number {
  return resolveBasicAttackPhaseTiming(weaponId === 'CATALYST_REFERENCE' ? 'BOW_REFERENCE' : weaponId, cycleS).postHitS;
}
